import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Grid } from '@material-ui/core'
import SydLabel from '../../../../../../../../commonDesign/SydLabel'
import SydSelect from '../../../../../../../../commonDesign/SydSelect'
import SydInput from '../../../../../../../../commonDesign/SydInput'
import Switch from '../../../../../../../../molecules/Switch'

const labelPositionOptions = [
  { label: 'Auto', value: '__auto__' },
  { label: 'Top', value: 'top' },
  { label: 'Bottom', value: 'bottom' },
  { label: 'Left', value: 'left' },
  { label: 'Right', value: 'right' },
  { label: 'Inside', value: 'inside' },
  { label: 'Outside', value: 'outside' }
]

const legendPositionOptions = [
  { label: 'Top', value: 'top' },
  { label: 'Bottom', value: 'bottom' },
  { label: 'Left', value: 'left' },
  { label: 'Right', value: 'right' }
]

const EMPTY_OBJ = {}

function DisplayOptionsEditor ({ display = {}, onChange }) {
  const label = useMemo(() => display.label || EMPTY_OBJ, [display.label])
  const legend = useMemo(() => display.legend || EMPTY_OBJ, [display.legend])
  const grid = useMemo(() => display.grid || EMPTY_OBJ, [display.grid])

  const handleLabelChange = useCallback((field, value) => {
    onChange({ display: { ...display, label: { ...label, [field]: value } } })
  }, [onChange, display, label])

  const handleLegendChange = useCallback((field, value) => {
    onChange({ display: { ...display, legend: { ...legend, [field]: value } } })
  }, [onChange, display, legend])

  const handleGridChange = useCallback((field, value) => {
    onChange({ display: { ...display, grid: { ...grid, [field]: value } } })
  }, [onChange, display, grid])

  return (
    <div>
      <h4 style={{ margin: '0 0 12px 0' }}>Display Options</h4>

      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontSize: '0.875rem', fontWeight: 500, marginBottom: '8px' }}>Labels</div>
        <Grid container spacing={2}>
          <Grid item xs={4} md={2}>
            <SydLabel label='Show'>
              <Switch
                checked={label.show !== false}
                onChange={(e) => handleLabelChange('show', e.target.checked)}
              />
            </SydLabel>
          </Grid>
          <Grid item xs={8} md={3}>
            <SydLabel label='Position'>
              <SydSelect
                minWidth='0'
                options={labelPositionOptions}
                value={label.position || '__auto__'}
                onChange={(e) => handleLabelChange('position', e.target.value === '__auto__' ? null : e.target.value)}
              />
            </SydLabel>
          </Grid>
          <Grid item xs={8} md={3}>
            <SydLabel label='Format' helperText='e.g. human, marketValue, percentage, currency'>
              <SydInput
                minWidth='0'
                value={label.format || ''}
                onChange={(e) => handleLabelChange('format', e.target.value || null)}
                placeholder='None'
              />
            </SydLabel>
          </Grid>
          <Grid item xs={6} md={2}>
            <SydLabel label='Rotate'>
              <SydInput
                minWidth='0'
                type='number'
                value={label.rotate || 0}
                onChange={(e) => handleLabelChange('rotate', Number(e.target.value) || 0)}
                placeholder='0'
              />
            </SydLabel>
          </Grid>
          <Grid item xs={6} md={2}>
            <SydLabel label='Font Size'>
              <SydInput
                minWidth='0'
                type='number'
                value={label.fontSize || ''}
                onChange={(e) => handleLabelChange('fontSize', e.target.value ? Number(e.target.value) : null)}
                placeholder='Auto'
              />
            </SydLabel>
          </Grid>
        </Grid>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontSize: '0.875rem', fontWeight: 500, marginBottom: '8px' }}>Legend</div>
        <Grid container spacing={2}>
          <Grid item xs={4} md={2}>
            <SydLabel label='Show'>
              <Switch
                checked={!!legend.show}
                onChange={(e) => handleLegendChange('show', e.target.checked)}
              />
            </SydLabel>
          </Grid>
          <Grid item xs={8} md={3}>
            <SydLabel label='Position'>
              <SydSelect
                minWidth='0'
                options={legendPositionOptions}
                value={legend.position || 'bottom'}
                onChange={(e) => handleLegendChange('position', e.target.value)}
              />
            </SydLabel>
          </Grid>
        </Grid>
      </div>

      <div>
        <div style={{ fontSize: '0.875rem', fontWeight: 500, marginBottom: '8px' }}>Margins</div>
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <SydLabel label='Top'>
              <SydInput
                minWidth='0'
                value={grid.top ?? '10%'}
                onChange={(e) => handleGridChange('top', e.target.value ?? '10%')}
                placeholder='10%'
              />
            </SydLabel>
          </Grid>
          <Grid item xs={6} md={3}>
            <SydLabel label='Right'>
              <SydInput
                minWidth='0'
                value={grid.right ?? '10%'}
                onChange={(e) => handleGridChange('right', e.target.value ?? '10%')}
                placeholder='10%'
              />
            </SydLabel>
          </Grid>
          <Grid item xs={6} md={3}>
            <SydLabel label='Bottom'>
              <SydInput
                minWidth='0'
                value={grid.bottom ?? '10%'}
                onChange={(e) => handleGridChange('bottom', e.target.value ?? '10%')}
                placeholder='10%'
              />
            </SydLabel>
          </Grid>
          <Grid item xs={6} md={3}>
            <SydLabel label='Left'>
              <SydInput
                minWidth='0'
                value={grid.left ?? '10%'}
                onChange={(e) => handleGridChange('left', e.target.value ?? '10%')}
                placeholder='10%'
              />
            </SydLabel>
          </Grid>
        </Grid>
      </div>
    </div>
  )
}

DisplayOptionsEditor.propTypes = {
  display: PropTypes.object,
  onChange: PropTypes.func.isRequired
}

export default DisplayOptionsEditor
